import React, {
  RefObject,
  forwardRef,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';

import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { Announcement } from 'mintel-react-ui';

import BlockAnalyst from '/components/BlockAnalyst';
import BlockAnalystFeedback from '/components/BlockAnalystFeedback';
import BlockRecommendedContent from '/components/BlockRecommendedContent';
import BlockWrapper from '/components/BlockWrapper';
import Carousel from '/components/Carousel';
import {
  ArticleBlockJson,
  ArticleProps,
  BlockProjectData,
  NullableHTMLDivElement,
} from '/types';
import {
  AppShellContext,
  MetadataContext,
  PresentModeContext,
  PublishStatusContext,
} from '/utils/contexts';
import { createBlockProjectData } from '/utils/functions';
import useTranslate, { Trans } from '/utils/translations';
import usePresentControls from '/utils/usePresentControls';
import useTracking from '/utils/useTracking';

import styles from './Article.scss';
import highlightOnArticle from './highlightOnArticle';

type BlockGroup = { block: ArticleBlockJson; idx: number }[];

export function* groupLinkedBlocks(
  blocks: ArticleBlockJson[],
): Generator<BlockGroup> {
  let linkedBlocks = [];
  for (let idx = 0; idx < blocks.length; ++idx) {
    const block = blocks[idx];
    if (block.linked_to_next) {
      linkedBlocks.push({ idx: idx, block: block });
    } else {
      if (linkedBlocks.length) {
        linkedBlocks.push({ idx: idx, block: block });
        yield linkedBlocks;
        linkedBlocks = [];
      } else {
        yield [{ idx: idx, block: block }];
      }
    }
  }
}

const Article = forwardRef<NullableHTMLDivElement[], ArticleProps>(
  ({ content, onScreenBlock, sendSnackbarEvent }, ref) => {
    const { portalJwt } = useContext(AppShellContext);
    const metadata = useContext(MetadataContext);
    const { trackRecommendedContent } = useTracking();
    const blocks = [...groupLinkedBlocks(content)];
    const presentMode = useContext(PresentModeContext);
    const publishStatus = useContext(PublishStatusContext);
    const showAnalystBlock = !isEmpty(metadata.editor);
    const numBlocks = content.length + (showAnalystBlock ? 1 : 0);
    const t = useTranslate();
    const { PresentControls: BlockAnalystPresentControls } = usePresentControls(
      {
        ref: ref as RefObject<NullableHTMLDivElement[]>,
        index: numBlocks - 1,
        numBlocks: numBlocks,
        onScreenBlock,
      },
    );
    const blocksProjectData: BlockProjectData[] = content.map((block) => {
      return createBlockProjectData(metadata.id, block.id, block.type);
    });
    const migrationAnnouncement = {
      mode: 'info',
      label: t('This report was reformatted since its original publication'),
      children: (
        <Trans i18nKey="migrationAnnouncement">
          <a href="https://help.mintel.com/en/articles/8320551-we-re-elevating-your-experience-with-the-new-mintel-reports-format">
            Click here
          </a>{' '}
          for more information.
        </Trans>
      ),
    };
    const aiTranslationAnnouncement = {
      mode: 'info',
      label: t(
        'This content has been translated by AI from its original version.',
      ),
      children: (
        <Trans i18nKey="aiTranslationAnnouncement">
          <a
            href="#"
            data-intercom-target={`${metadata.language}-ai-translation`}
          >
            Click here
          </a>{' '}
          for more information.
        </Trans>
      ),
    };
    const [
      migrationAnnouncementClosed,
      setMigrationAnnouncementClosed,
    ] = useState(false);
    const [
      aiTranslationAnnouncementClosed,
      setAiTranslationAnnouncementClosed,
    ] = useState(false);

    useLayoutEffect(() => {
      if (metadata.highlight_terms.length) {
        highlightOnArticle(metadata.highlight_terms.map((x) => x[1]));
      }
    }, [metadata]);

    return (
      <article
        id={metadata.id}
        lang={metadata.language || 'en'}
        className={classNames({
          [styles.article]: !presentMode,
          [styles['japanese-fonts']]: metadata.language === 'ja',
          [styles['thai-fonts']]: metadata.language === 'th',
        })}
      >
        {!migrationAnnouncementClosed && metadata.migrated_from_oxygen && (
          <Announcement
            {...migrationAnnouncement}
            onClose={(): void => setMigrationAnnouncementClosed(true)}
          />
        )}
        {!aiTranslationAnnouncementClosed && metadata.ai_translation && (
          <Announcement
            {...aiTranslationAnnouncement}
            onClose={(): void => setAiTranslationAnnouncementClosed(true)}
          />
        )}
        {blocks.map((blockGroup: BlockGroup) => {
          if (blockGroup.length > 1) {
            const activeBlock = blockGroup.findIndex(
              ({ block }) => block.id === onScreenBlock?.current,
            );
            const carouselBlocks = blockGroup.map(({ block, idx }) => (
              <BlockWrapper
                ref={ref}
                onScreenBlock={onScreenBlock}
                index={idx}
                key={block.id}
                blockProps={block}
                numBlocks={numBlocks}
                sendSnackbarEvent={sendSnackbarEvent}
                blocksProjectData={blocksProjectData}
              />
            ));
            return presentMode ? (
              <>{carouselBlocks}</>
            ) : (
              <Carousel activeSlide={activeBlock}>{carouselBlocks}</Carousel>
            );
          } else {
            const singleBlock = blockGroup[0];
            return (
              <BlockWrapper
                ref={ref}
                onScreenBlock={onScreenBlock}
                index={singleBlock.idx}
                key={singleBlock.block.id}
                blockProps={singleBlock.block}
                numBlocks={numBlocks}
                sendSnackbarEvent={sendSnackbarEvent}
                blocksProjectData={blocksProjectData}
              />
            );
          }
        })}
        {!presentMode && <BlockAnalystFeedback />}
        {showAnalystBlock && (
          <BlockAnalystPresentControls>
            <BlockAnalyst
              articleTitle={metadata.title}
              editor={metadata.editor}
            />
          </BlockAnalystPresentControls>
        )}
        {portalJwt && !presentMode && publishStatus === 'live' && (
          <BlockRecommendedContent
            articleId={metadata.id}
            trackRecommendedContent={trackRecommendedContent}
          />
        )}
      </article>
    );
  },
);

Article.displayName = 'components/Article';
export default Article;
